import { themeSelector, userUserRoleAtom } from 'atoms'
import { useEffect, useMemo, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { DEFAULT_ORG_THEME } from 'components/Admin/Organizations/ThemeModal'
import { appColors } from 'utils'

export const NAVBAR_HEIGHT = 61

function useInnovaTheme() {
  const _isMounted = useRef()
  const [theme, setTheme] = useRecoilState(themeSelector)
  const userRole = useRecoilValue(userUserRoleAtom)
  const orgTheme = userRole?.userPrefs?.organizationTheme

  useEffect(() => {
    _isMounted.current = true

    return () => {
      _isMounted.current = false
    }
  }, [])

  const getAgGridTheme = () => {
    return theme === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'
  }

  const searchBarStyle = (flex = 1) => ({
    flex: flex,
    backgroundColor: theme === 'dark' ? '#181d1f' : '#f0f0f0',
    focusedColor: theme === 'dark' ? '#3C3C3C' : '#f0f0f0',
    border: theme === 'dark' ? '' : '1px solid #ccc',
    size: 'large',
  })

  const ribbonStyle = useMemo(() => {
    return {
      backgroundColor: theme === 'dark' ? '#202224' : '#4c85c2',
      secondaryBackground: theme === 'dark' ? '#181C1E' : '#4b678c',
      color: theme === 'dark' ? '#fff' : '#fff',
      defaultBorderColor: theme === 'dark' ? '#2f3a45' : '#4b678c',
      activeBorderColor: theme === 'dark' ? '#c0c0c0' : '#fff',
      disabledColor: theme === 'dark' ? '#919191' : '#c0c0c0',
      border: theme === 'dark' ? '' : '1px solid #ccc',
      selectedColor: theme === 'dark' ? appColors.itemTextColor : '#fff',
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  const getBackColor = () => {
    return theme === 'dark' ? appColors.itemBackColor : '#fff'
  }

  const getChartBackColor = () => {
    return theme === 'dark' ? '#202224' : '#f5f5f5'
  }

  const getWindowBarColor = () => {
    return theme === 'dark' ? '#252A2D' : '#385d85'
  }

  const getSettingsBackgroundColor = () => {
    return theme === 'dark' ? '#24282B ' : '#517193'
  }

  // *** we may want to use this instead of gray + blue
  const getSettingsGrayWhiteBackgroundColor = () => {
    return theme === 'dark' ? '#24282B' : '#f5f5f5'
  }

  const getSettingsFieldStyle = () => {
    return {
      color: '#fff',
      borderColor: 'rgba(255,255,255,0.15)',
      backgroundColor: getSettingsBackgroundColor(),
      focusedBorderColor: 'rgba(255,255,255,0.21)',
      background: 'rgba(255,255,255,0.04)',
    }
  }

  const getCardBackground = () => {
    return theme === 'dark' ? '#252A2D' : '#f5f5f5'
  }

  const getCardContrastColor = () => {
    return theme === 'dark' ? '#181C1E' : '#f5f5f5'
  }

  const getTreeBackColor = () => {
    return theme === 'dark' ? '#24282B' : '#f5f5f5'
  }

  const getRibbonBackgroundColor = () => {
    return theme === 'dark' ? '#202224' : '#4c85c2'
  }

  const getActionBarColor = () => {
    let userTheme = theme === 'dark' ? orgTheme?.darkThemePrimaryCol : orgTheme?.lightThemePrimaryCol
    if (userTheme) return userTheme

    return theme === 'dark' ? DEFAULT_ORG_THEME.darkThemePrimaryCol : DEFAULT_ORG_THEME.lightThemePrimaryCol
  }

  // *** this needs work
  const getActionBarActiveColor = () => {
    return theme === 'dark' ? '#323538' : '#ead4d0'
  }

  const getTextColor = () => {
    return theme === 'dark' ? '#FFFFFF' : '#000000'
  }

  const getDisabledColor = () => {
    return theme === 'dark' ? '#999' : '#333'
  }

  const getAccentColor = () => {
    return theme === 'dark'
      ? orgTheme?.darkThemeAccentCol || DEFAULT_ORG_THEME.darkThemeAccentCol
      : orgTheme?.lightThemeAccentCol || DEFAULT_ORG_THEME.lightThemeAccentCol
  }

  const getLogoColor = () => {
    let userTheme = theme === 'dark' ? orgTheme?.darkThemeLogoCol : orgTheme?.lightThemeLogoCol
    if (userTheme) return userTheme

    return theme === 'dark' ? DEFAULT_ORG_THEME.darkThemeLogoCol : DEFAULT_ORG_THEME.lightThemeLogoCol
  }

  const getLinearGradient = () => {
    return theme === 'dark'
      ? 'linear-gradient(to bottom, #2d2d2d 0%, #1c1c1c 100%)'
      : 'linear-gradient(to bottom, #ffffff 0%, #C0C0C0 100%)'
  }

  const getNavBarBackground = () => {
    if (orgTheme?.navBarGradientCols?.length > 0) {
      const colors =
        orgTheme?.navBarGradientCols.length === 1
          ? [orgTheme?.navBarGradientCols[0], orgTheme?.navBarGradientCols[0]]
          : orgTheme?.navBarGradientCols
      return `linear-gradient(90deg, ${colors.join(', ')})`
    }

    return theme === 'dark' ? '#111924' : '#2B579A'
  }

  const getGrayColor = (alpha = 0.08) => (theme === 'dark' ? `rgba(255,255,255,${alpha})` : `rgba(0,0,0,${alpha})`)

  const getTooltipBgColor = () => {
    return theme === 'dark' ? 'rgb(19,62,96)' : '#2d2d2d'
  }

  const getSuccessStyle = (themeOverride) => {
    const effectiveTheme = themeOverride || theme
    return effectiveTheme === 'dark'
      ? {
          color: '#00dc78',
          backgroundColor: 'rgba(0,220,120,0.07)',
          borderColor: '#00DC78CC',
          hover: { backgroundColor: 'rgba(0,220,120,0.12)' },
        }
      : {
          color: '#00a046',
          backgroundColor: 'rgba(0,160,70,0.06)',
          borderColor: '#DBF2E5',
          hover: { backgroundColor: 'rgba(0,160,70,0.10)' },
        }
  }

  const getErrorStyle = (themeOverride) => {
    const effectiveTheme = themeOverride || theme
    return effectiveTheme === 'dark'
      ? {
          color: '#f44336',
          backgroundColor: 'rgba(244,67,54,0.07)',
          borderColor: '#F4433633',
          hover: { backgroundColor: 'rgba(244,67,54,0.12)' },
        }
      : {
          color: '#d32f2f',
          backgroundColor: 'rgba(211,47,47,0.06)',
          borderColor: '#F2DEDE',
          hover: { backgroundColor: 'rgba(211,47,47,0.10)' },
        }
  }

  const getBorderColor = (themeOverride) => {
    const effectiveTheme = themeOverride || theme
    return effectiveTheme === 'dark' ? '#c0c0c0' : '#505050'
  }

  return {
    getNavBarBackground,
    getLinearGradient,
    getAccentColor,
    getLogoColor,
    toggleTheme,
    theme,
    getAgGridTheme,
    searchBarStyle,
    getBackColor,
    getActionBarActiveColor,
    getCardBackground,
    getCardContrastColor,
    getChartBackColor,
    getWindowBarColor,
    getSettingsBackgroundColor,
    getSettingsGrayWhiteBackgroundColor,
    getSettingsFieldStyle,
    getTreeBackColor,
    getActionBarColor,
    getTextColor,
    getRibbonBackgroundColor,
    ribbonStyle,
    getDisabledColor,
    getGrayColor,
    getTooltipBgColor,
    getSuccessStyle,
    getErrorStyle,
    getBorderColor,
  }
}

export default useInnovaTheme
